import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '@/components/PageHero';
import FadeInUp from '@/components/FadeInUp';
import { ClockIcon, CheckIcon, ArrowRight, RocketIcon, ForkliftIcon, CertificateIcon } from '@/components/icons';

export default function BeginnerCourse() {
  return (
    <>
      <PageHero
        eyebrow="Beginner Course"
        title="Starting from scratch? We’ve got you."
        subtitle="For trainees with little or no forklift experience. A 2-day programme combining one-on-one practical training with our full WorkSafeNZ standard course."
        image="/generated/course-beginner.png"
        breadcrumbs={[{label:'Home', to:'/'}, {label:'Courses', to:'/courses'}, {label:'Beginner'}]}
        badge="Available for Counterbalance & Reach Truck"
      />

      <section className="nft-container py-16 lg:py-20">
        <FadeInUp>
          <div className="label-eyebrow text-[#FC4E2C] mb-3">Who it’s for</div>
          <h2 className="font-display font-extrabold uppercase text-3xl lg:text-5xl text-[#060D9C] leading-[0.95] max-w-3xl">
            For those seeking their <span className="text-[#FC4E2C]">first forklift certificate.</span>
          </h2>
          <p className="mt-4 text-[#060D9C]/80 max-w-3xl text-base lg:text-lg">
            Our beginner stream gives you proper one-on-one practical time before you sit the standard course alongside experienced operators. You finish confident, certified, and competent.
          </p>
          <span className="section-rule mt-6" />
        </FadeInUp>

        {/* Programme timeline */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="beginner-programme-timeline">
          <FadeInUp>
            <div className="surface-card overflow-hidden h-full">
              <div className="bg-[#060D9C] text-white p-6">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow text-[#FC4E2C]">Day One · Tuesday</div>
                  <div className="font-mono-cert text-[#FC4E2C] text-xl">01</div>
                </div>
                <h3 className="mt-2 font-display font-extrabold uppercase text-3xl leading-tight">Practical Training Session</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center">
                    <RocketIcon width={20} height={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#060D9C]/85"><strong>1-on-1 supervised practical</strong> with an NFT trainer. Build real handling skills in a safe, controlled environment.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center">
                    <ClockIcon width={20} height={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#060D9C]/85"><strong>~2 hours</strong>. Choose from <strong>8:00am</strong>, <strong>10:30am</strong>, or <strong>1:00pm</strong> (subject to availability).</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center">
                    <ForkliftIcon width={20} height={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#060D9C]/85">Recommended to begin on a <strong>Ride-on Counterbalance</strong>. Reach Truck follows once competent.</p>
                  </div>
                </div>
              </div>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.08}>
            <div className="surface-card overflow-hidden h-full">
              <div className="bg-[#FC4E2C] text-white p-6">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow">Day Two · Wed / Thu / Fri</div>
                  <div className="font-mono-cert text-xl">02</div>
                </div>
                <h3 className="mt-2 font-display font-extrabold uppercase text-3xl leading-tight">Full Standard Course</h3>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-[#060D9C]/85">
                  Attend the full WorkSafeNZ standard course at our East Tamaki premises:
                </p>
                <ul className="space-y-2 text-sm">
                  {['Introduction & video presentation','Quiz on video contents','Theory test parts 1 & 2','Pre-operation checks','Practical driving assessment','Summary & certification'].map(t => (
                    <li key={t} className="flex items-start gap-2">
                      <span className="shrink-0 w-5 h-5 rounded bg-[#2D8653]/15 text-[#2D8653] flex items-center justify-center">
                        <CheckIcon width={12} height={12} />
                      </span>
                      <span className="text-[#060D9C]/85">{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-2 text-sm pt-2 border-t border-[#060D9C]/10">
                  <ClockIcon width={16} height={16} className="text-[#FC4E2C]" />
                  <span className="text-[#060D9C]/85">~6–7 hours total (varies with class size)</span>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>

        {/* Outcome */}
        <FadeInUp>
          <div className="mt-12 surface-card p-6 lg:p-8">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-[#2D8653]/15 text-[#2D8653] flex items-center justify-center">
                <CertificateIcon width={26} height={26} />
              </div>
              <div>
                <div className="label-eyebrow text-[#FC4E2C] mb-1">Outcome / Certification</div>
                <h3 className="font-display font-extrabold uppercase text-2xl lg:text-3xl text-[#060D9C] leading-tight">
                  WorkSafeNZ approved Beginner Certificate.
                </h3>
                <ul className="mt-4 space-y-2 text-sm text-[#060D9C]/85">
                  <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> Personal ID licence card with photo</li>
                  <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> Certificate states attendance & passing of a 2-day Beginner course</li>
                  <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> Additional equipment can be added within 3 years for a small extra cost</li>
                  <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> Extra practice available on an hourly basis if needed</li>
                  <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> Full Competenz backing; exceeds NZ Department of Labour requirements</li>
                </ul>
              </div>
            </div>
          </div>
        </FadeInUp>

        {/* WINZ note */}
        <FadeInUp>
          <div className="mt-6 callout-warning" data-testid="beginner-winz-callout">
            <div className="label-eyebrow text-[#060D9C] mb-1">Combined Beginner Course (Counterbalance + Reach Truck)</div>
            <p className="text-sm text-[#060D9C]/85">
              Beginner courses are available for both Counterbalance and Reach Truck combined — contact us for details. <strong>WINZ quotations available on request.</strong>
            </p>
          </div>
        </FadeInUp>

        <div className="mt-10 text-center">
          <Link to="/booking" className="btn-primary-orange">
            Book a Beginner Course <ArrowRight width={18} height={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
